/**
 * The one client for "what will this operation cost?".
 *
 * Backed by POST /api/v1/cost/preview, which replaced three endpoints that
 * answered the same question in two different units: /tokens/estimate (Cloud
 * Run only, keyed on estimator names nothing published), /cost/estimate +
 * /cost/compare + /cost/pricing (USD, video only), and /cost/broll-estimate.
 *
 * An operation is named the way GET /api/v1/capabilities names it — the method
 * and the path you are about to call — so there is no second vocabulary to keep
 * in step with the routes.
 */

import { buildCloudApiUrl } from './urlResolver';
import { getAuthHeadersFromStorage, handleResponse, JSON_HEADERS } from '../utils/httpUtils';

/** The estimator's breakdown, flattened with the headline figures on top. */
export interface CostEstimate {
  estimated_tokens: number;
  estimated_cost_usd: number;
  local_gpu_tokens: number;
  local_gpu_cost_usd: number;
  processing_seconds: number;
  is_ceiling?: boolean;
  /**
   * True when the selected provider runs on the user's own hardware (e.g. local
   * TTS like kokoro). On desktop these run on the user's GPU and are free, so the
   * card shows the local_gpu price ("Free") instead of the cloud price. On Cloud
   * Run the same providers run on Asgard's GPU and stay billable at cloud rate.
   */
  is_local_provider?: boolean;
  [key: string]: unknown;
}

export interface CostPreviewEnvelope {
  estimated_tokens: number;
  estimated_cost_usd: number;
  /**
   * Whether running this request charges the signed-in account. Exactly
   * `estimated_tokens > 0` — the server derives one from the other.
   */
  billed: boolean;
  /**
   * Who pays for THIS request on THIS server, resolved:
   * `platform_paid` | `byok_free` | `no_spend` | `unavailable_here`.
   *
   * Not nullable. It used to be, because the field published the ROUTE's label
   * and a route that charges deeper in its pipeline carries none — so a 9,100
   * token B-roll quote arrived with `spend_policy: null`. The server now
   * resolves it per request and always has an answer; declaring a null it can
   * no longer send would leave this file the last thing in the system that
   * believes in one (RULE #1.5).
   */
  spend_policy: string;
  /** Why that policy applies, in prose meant to be shown to a person. */
  spend_reason: string;
  estimate: Record<string, unknown>;
}

/**
 * Ask what one operation will cost, without running it.
 *
 * @param path   OpenAPI path of the operation, e.g. '/api/v1/generate/tts'.
 * @param parameters The request parameters that move the price.
 * @param method HTTP method of the operation (defaults to POST).
 * @returns The /cost/preview envelope payload: headline figures and the
 *          estimator's own breakdown under ``estimate``.
 * @throws When the server rejects the request — the caller decides whether to
 *         block submission or show the failure.
 */
export async function fetchCostPreview(
  path: string,
  parameters: Record<string, unknown>,
  method: string = 'POST',
  signal?: AbortSignal
): Promise<CostPreviewEnvelope> {
  const authHeaders = await getAuthHeadersFromStorage();
  const response = await fetch(buildCloudApiUrl('/api/v1/cost/preview'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...(authHeaders as Record<string, string>) },
    body: JSON.stringify({ method, path, parameters }),
    signal,
  });
  // handleResponse reads the body as JSON only when it is the API's envelope.
  // Parsing first is what turned an unreachable server -- Cloud Run answering
  // with Google's HTML 503 page -- into "Unexpected token '<'" on screen.
  return handleResponse<CostPreviewEnvelope>(response);
}

/**
 * The single-operation view of :func:`fetchCostPreview`: the estimator's
 * breakdown with the headline token/USD figures hoisted over it.
 */
export async function previewCost(
  path: string,
  parameters: Record<string, unknown>,
  method: string = 'POST',
  signal?: AbortSignal
): Promise<CostEstimate> {
  const data = await fetchCostPreview(path, parameters, method, signal);
  return {
    ...(data.estimate as Record<string, unknown>),
    estimated_tokens: data.estimated_tokens,
    estimated_cost_usd: data.estimated_cost_usd,
  } as CostEstimate;
}
